import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, NavLink, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Home from "./Home";
import About from "./About";
import Services from "./Services";
import TaxCalculator from "./TaxCalculator";
import Contact from "./Contact";
import AppInstallPrompt from "./AppInstallPrompt";

export const SITE_URL = "https://axiscompliancesolutions.com";
export const PHONE_DISPLAY = "+92-331-4441076";
export const PHONE_TEL = "+923314441076";
export const EMAIL = "complianceaxis9@gmail.com";
export const ADDRESS = "17 BS 30/4, Maqbool Road, Muhammadi Chock Ichra, Lahore.";
export const WHATSAPP_URL = "https://wa.me/923314441076";
export const SLOGAN = "Smart Finance, Clear Compliance";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/tax-calculator", label: "Tax Calculator" },
  { to: "/contact", label: "Contact" },
];

function NavigationLink({ to, label, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `nav-link ${isActive ? "nav-link-active" : "text-slate-700 hover:text-slate-950"}`
      }
    >
      {label}
    </NavLink>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <Router>
      <Helmet>
        <meta name="theme-color" content="#0f3d5e" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Axis Compliance Solutions" />
        <meta name="application-name" content="Axis Compliance Solutions" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="og:site_name" content="Axis Compliance Solutions" />
        <meta property="og:locale" content="en_PK" />
        <link rel="manifest" href="/manifest.json" />
        <link rel="sitemap" type="application/xml" href="/sitemap.xml" />
        <link rel="alternate" type="text/plain" href="/llms.txt" title="AI summary" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "AccountingService",
                "@id": `${SITE_URL}/#business`,
                name: "Axis Compliance Solutions",
                url: SITE_URL,
                logo: `${SITE_URL}/axis-logo.png`,
                image: `${SITE_URL}/hero-tax.jpg`,
                description:
                  "Smart Finance, Clear Compliance. Consulting firm with international bookkeeping and bank reconciliation experience plus nationwide taxation, FBR, SECP, SRB, PRA, BRA, NTN, STRN and compliance advisory services in Pakistan.",
                telephone: PHONE_DISPLAY,
                email: EMAIL,
                address: {
                  "@type": "PostalAddress",
                  streetAddress: "17 BS 30/4, Maqbool Road, Muhammadi Chock Ichra",
                  addressLocality: "Lahore",
                  addressRegion: "Punjab",
                  addressCountry: "PK",
                },
                areaServed: ["Lahore", "Punjab", "Pakistan"],
                priceRange: "$$",
                sameAs: [WHATSAPP_URL],
                knowsAbout: [
                  "Income tax filing Pakistan",
                  "SECP company registration",
                  "FBR compliance",
                  "Sales tax return filing",
                  "International bookkeeping",
                  "Bank reconciliation",
                  "Salary tax calculation Pakistan",
                ],
              },
              {
                "@type": "WebSite",
                "@id": `${SITE_URL}/#website`,
                name: "Axis Compliance Solutions",
                url: SITE_URL,
                publisher: {
                  "@id": `${SITE_URL}/#business`,
                },
                inLanguage: "en-PK",
              },
            ],
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-stone-50 text-slate-900 antialiased">
        <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="bg-slate-950 text-white">
            <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-2 text-xs sm:flex-row sm:items-center sm:justify-between lg:px-8">
              <p>{SLOGAN} - international bookkeeping plus nationwide tax and compliance advisory.</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <a className="hover:text-cyan-200" href={`tel:${PHONE_TEL}`}>
                  {PHONE_DISPLAY}
                </a>
                <a className="hover:text-cyan-200" href={`mailto:${EMAIL}`}>
                  {EMAIL}
                </a>
              </div>
            </div>
          </div>

          <div className="mx-auto flex max-w-7xl items-center justify-between gap-5 px-4 py-4 lg:px-8">
            <Link to="/" className="flex min-w-0 items-center gap-3 sm:gap-4" onClick={() => setMenuOpen(false)}>
              <img src="/axis-mark-transparent.png" alt="" className="h-16 w-auto shrink-0 object-contain sm:h-20 lg:h-24" />
              <span className="min-w-0 leading-none">
                <span className="block text-3xl font-extrabold tracking-[0.08em] text-[#08254a] sm:text-4xl lg:text-5xl">
                  AXIS
                </span>
                <span className="mt-1 block whitespace-nowrap text-xs font-bold uppercase tracking-[0.16em] text-[#08254a] sm:text-sm lg:text-base">
                  Compliance Solutions
                </span>
              </span>
            </Link>

            <nav className="hidden flex-wrap items-center justify-end gap-1 md:flex">
              {navItems.map((item) => (
                <NavigationLink key={item.to} {...item} />
              ))}
              <Link to="/contact" className="ml-3 rounded-md bg-cyan-800 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-900">
                Book Consultation
              </Link>
            </nav>

            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-300 text-slate-800 md:hidden"
              aria-label="Toggle navigation menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="text-xl leading-none">{menuOpen ? "x" : "="}</span>
            </button>
          </div>

          {menuOpen && (
            <nav className="border-t border-slate-200 bg-white px-4 py-4 shadow-lg md:hidden">
              <div className="flex flex-col gap-2">
                {navItems.map((item) => (
                  <NavigationLink key={item.to} {...item} onClick={() => setMenuOpen(false)} />
                ))}
                <Link
                  to="/contact"
                  onClick={() => setMenuOpen(false)}
                  className="mt-2 rounded-md bg-cyan-800 px-4 py-3 text-center text-sm font-semibold text-white"
                >
                  Book Consultation
                </Link>
              </div>
            </nav>
          )}
        </header>

        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/tax-calculator" element={<TaxCalculator />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </main>

        <footer className="bg-slate-950 text-white">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:grid-cols-2 lg:grid-cols-4 lg:px-8">
            <div className="sm:col-span-2">
              <div className="flex items-center gap-4">
                <img src="/axis-mark-transparent.png" alt="" className="h-20 w-auto object-contain brightness-0 invert sm:h-24" />
                <div className="leading-none">
                  <p className="text-3xl font-extrabold tracking-[0.08em] text-white sm:text-4xl">AXIS</p>
                  <p className="mt-2 whitespace-nowrap text-xs font-bold uppercase tracking-[0.16em] text-cyan-100 sm:text-sm">
                    Compliance Solutions
                  </p>
                </div>
              </div>
              <p className="mt-4 max-w-xl text-sm leading-6 text-slate-300">
                <strong className="block text-cyan-200">{SLOGAN}</strong>
                International bookkeeping and bank reconciliation support, paired with nationwide
                taxation, SECP registration, FBR compliance, audit support and business advisory
                services in Pakistan.
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-cyan-200">Services</h3>
              <ul className="mt-4 space-y-2 text-sm text-slate-300">
                <li><Link className="hover:text-white" to="/services">Income tax filing</Link></li>
                <li><Link className="hover:text-white" to="/services">Company registration</Link></li>
                <li><Link className="hover:text-white" to="/services">FBR and SECP compliance</Link></li>
                <li><Link className="hover:text-white" to="/services">Bookkeeping and bank reconciliation</Link></li>
                <li><Link className="hover:text-white" to="/tax-calculator">Salary tax calculator</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-cyan-200">Contact</h3>
              <ul className="mt-4 space-y-2 text-sm text-slate-300">
                <li>{ADDRESS}</li>
                <li><a className="hover:text-white" href={`tel:${PHONE_TEL}`}>{PHONE_DISPLAY}</a></li>
                <li><a className="hover:text-white" href={`mailto:${EMAIL}`}>{EMAIL}</a></li>
                <li><a className="hover:text-white" href={WHATSAPP_URL} target="_blank" rel="noreferrer">WhatsApp consultation</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 px-4 py-4 text-center text-xs text-slate-400">
            Copyright {new Date().getFullYear()} Axis Compliance Solutions. All rights reserved.
          </div>
        </footer>

        <AppInstallPrompt />
      </div>
    </Router>
  );
}

export default App;
